// Lifecycle and deploy marks, drawn over a time series as labelled reference
// lines.
//
// **Why an overlay rather than a series.** The mark set is a different query
// over a different source with a different row shape; feeding it through the
// chart primitive as a series would mean inventing a y value for an event that
// has none, and whatever number was invented would be read off the axis as
// data. A reference line makes the only claim an event supports: this happened
// HERE on the time axis.
//
// The horizontal position is `annotationMarks`'s arithmetic over the same
// buckets the series was drawn from — `point` mapping for a line, `step` for a
// bar, because the two axes place a bucket in different places. The overlay
// spans the card's content box, which is the same box the chart SVG stretches
// across, so a mark at 0.5 sits over the middle bucket.
import { Tooltip } from "@bi/shared";

import { type AnnotationMark, foldNearbyMarks } from "../../lib/annotation-marks";

export interface AnnotationLayerProps {
  marks: readonly AnnotationMark[];
  /** How many marks fell outside the plotted window. Said out loud rather than
   *  quietly dropped: a reader looking for last month's deploy needs to know it
   *  is off the edge, not conclude it never happened. */
  outsideCount?: number;
}

/** Colour slot per event kind, assigned in first-seen order. Not a palette
 *  lookup by name: the `kind` column is whatever the events source puts in it,
 *  so a fixed mapping would leave every unrecognised kind uncoloured. */
function kindSlots(marks: readonly AnnotationMark[]): Map<string, number> {
  const slots = new Map<string, number>();
  for (const mark of marks) {
    if (!slots.has(mark.kind)) slots.set(mark.kind, slots.size);
  }
  return slots;
}

export function AnnotationLayer({ marks, outsideCount = 0 }: AnnotationLayerProps) {
  if (marks.length === 0 && outsideCount === 0) return null;
  const slots = kindSlots(marks);
  const folded = foldNearbyMarks(marks);
  return (
    <>
      <div className="bi-studio-marks" aria-hidden="true">
        {folded.map(({ mark, count }) => (
          <span
            key={`${mark.at}-${mark.label}`}
            className="bi-studio-mark"
            style={{
              left: `${(mark.position * 100).toFixed(3)}%`,
              // The palette slot is a token, so the marks re-tint with the
              // rest of the chart in either theme.
              ["--bi-mark-color" as string]: `var(--rc-chart-${(slots.get(mark.kind) ?? 0) % 8 + 1})`,
            }}
          >
            <span className="bi-studio-mark__label">
              {count > 1 ? `${mark.label} +${count - 1}` : mark.label}
            </span>
          </span>
        ))}
      </div>
      {/* The same information as text, because the overlay above is
          `aria-hidden` (it is positioning, not content) and a screen reader
          would otherwise be told a chart has no events on it. */}
      <p className="bi-card__note">
        <Tooltip label={marks.map((m) => `${m.at}: ${m.label}`).join("; ") || "No events in view"}>
          <span tabIndex={0}>
            {marks.length > 0
              ? `${marks.length} event${marks.length === 1 ? "" : "s"} marked on this chart`
              : "No events in this window"}
            {outsideCount > 0
              ? `, ${outsideCount} more outside it`
              : ""}
          </span>
        </Tooltip>
      </p>
    </>
  );
}
